const COLOR_TAG_PATTERN = /\[color\s*=\s*([^\]\r\n]+)\]|\[\/color\]/gi;
const COLOR_OPEN_PATTERN = /\[color\s*=\s*([^\]\r\n]+)\]/gi;
const COLOR_CLOSE_PATTERN = /\[\/color\]/gi;
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const NAMED_COLOR_PATTERN = /^[a-z][a-z0-9]*$/i;
const RGB_COLOR_PATTERN = /^rgba?\((.*)\)$/i;
const DRAFT_COLOR_STYLE_PREFIX = "color-";

function normalizeWhitespace(value = "") {
	return `${value}`.replace(/\s+/g, " ").trim();
}

function isValidRgbChannel(value) {
	if (!/^\d{1,3}$/.test(value)) {
		return false;
	}
	const numericValue = Number(value);
	return numericValue >= 0 && numericValue <= 255;
}

function isValidAlphaChannel(value) {
	if (/^(?:0|1)(?:\.\d+)?$/.test(value) || /^0?\.\d+$/.test(value)) {
		const numericValue = Number(value);
		return numericValue >= 0 && numericValue <= 1;
	}
	if (/^\d{1,3}%$/.test(value)) {
		const numericValue = Number(value.slice(0, -1));
		return numericValue >= 0 && numericValue <= 100;
	}
	return false;
}

function sanitizeRgbColor(value = "") {
	const match = normalizeWhitespace(value).match(RGB_COLOR_PATTERN);
	if (!match) {
		return null;
	}

	const fnName = value.trim().slice(0, value.trim().indexOf("(")).toLowerCase();
	const parts = match[1].split(",").map((part) => part.trim());
	if ((fnName === "rgb" && parts.length !== 3) || (fnName === "rgba" && parts.length !== 4)) {
		return null;
	}
	if (!parts.slice(0, 3).every(isValidRgbChannel)) {
		return null;
	}
	if (parts.length === 4 && !isValidAlphaChannel(parts[3])) {
		return null;
	}
	return `${fnName}(${parts.join(", ")})`;
}

export function sanitizeAmbifiMarkdownColor(value = "") {
	const normalized = normalizeWhitespace(value);
	if (!normalized || /[;"'<>]/.test(normalized) || /url\s*\(/i.test(normalized)) {
		return null;
	}
	if (HEX_COLOR_PATTERN.test(normalized)) {
		return normalized.toLowerCase();
	}
	const rgbColor = sanitizeRgbColor(normalized);
	if (rgbColor) {
		return rgbColor;
	}
	if (NAMED_COLOR_PATTERN.test(normalized)) {
		return normalized.toLowerCase();
	}
	return null;
}

function renderColorSyntax(source = "", renderOpen, renderClose) {
	const text = source == null ? "" : `${source}`;
	const root = [];
	const stack = [];
	let lastIndex = 0;
	let match;
	COLOR_TAG_PATTERN.lastIndex = 0;

	const appendToCurrent = (value) => {
		if (!value) {
			return;
		}
		if (stack.length > 0) {
			stack[stack.length - 1].children.push(value);
		} else {
			root.push(value);
		}
	};

	while ((match = COLOR_TAG_PATTERN.exec(text)) !== null) {
		appendToCurrent(text.slice(lastIndex, match.index));
		if (match[1] !== undefined) {
			const color = sanitizeAmbifiMarkdownColor(match[1]);
			if (color) {
				stack.push({
					color,
					rawOpen: match[0],
					children: []
				});
			} else {
				appendToCurrent(match[0]);
			}
		} else if (stack.length > 0) {
			const node = stack.pop();
			appendToCurrent(`${renderOpen(node.color)}${node.children.join("")}${renderClose(node.color)}`);
		} else {
			appendToCurrent(match[0]);
		}
		lastIndex = COLOR_TAG_PATTERN.lastIndex;
	}

	appendToCurrent(text.slice(lastIndex));

	while (stack.length > 0) {
		const node = stack.pop();
		appendToCurrent(`${node.rawOpen}${node.children.join("")}`);
	}

	return root.join("");
}

export function convertAmbifiMarkdownColorSyntaxToHtml(markdown = "") {
	return renderColorSyntax(
		markdown,
		(color) => `<span style="color:${color}">`,
		() => "</span>"
	);
}

function compactDraftColorValue(value) {
	const color = sanitizeAmbifiMarkdownColor(value);
	return color ? color.replace(/\s+/g, "") : null;
}

export function getAmbifiDraftColorStyle(value = "") {
	const color = compactDraftColorValue(value);
	return color ? `${DRAFT_COLOR_STYLE_PREFIX}${color}` : null;
}

function normalizeSelectionPosition(value, fallback, max) {
	const numericValue = Number(value);
	if (!Number.isFinite(numericValue)) {
		return fallback;
	}
	return Math.max(0, Math.min(numericValue, max));
}

export function wrapTextWithAmbifiMarkdownColor(value = "", color = "", selectionStart, selectionEnd, placeholder = "text") {
	const text = value == null ? "" : `${value}`;
	const safeColor = sanitizeAmbifiMarkdownColor(color) || "#ff0000";
	const start = normalizeSelectionPosition(selectionStart, text.length, text.length);
	const end = normalizeSelectionPosition(selectionEnd, start, text.length);
	const normalizedStart = Math.min(start, end);
	const normalizedEnd = Math.max(start, end);
	const selectedText = text.slice(normalizedStart, normalizedEnd) || placeholder;
	const openToken = `[color=${safeColor}]`;
	const closeToken = "[/color]";
	const nextText = `${text.slice(0, normalizedStart)}${openToken}${selectedText}${closeToken}${text.slice(normalizedEnd)}`;
	const nextSelectionStart = normalizedStart + openToken.length;

	return {
		text: nextText,
		selectionStart: nextSelectionStart,
		selectionEnd: nextSelectionStart + selectedText.length
	};
}

function appendMappedText(source, start, end, target, indexMap) {
	let nextTarget = target;
	for (let i = start; i < end; i++) {
		indexMap[i] = nextTarget.length;
		nextTarget += source[i];
	}
	return nextTarget;
}

function normalizeRangeNumber(value, fallback = 0) {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? numericValue : fallback;
}

function remapInlineStyleRanges(inlineStyleRanges = [], indexMap = []) {
	const remappedRanges = [];

	const flushRange = (range, start, end) => {
		if (start !== null && end >= start) {
			remappedRanges.push({
				offset: start,
				length: end - start + 1,
				style: range.style
			});
		}
	};

	inlineStyleRanges.forEach((range) => {
		if (!range || !range.style) {
			return;
		}

		const rangeStart = Math.max(0, normalizeRangeNumber(range.offset));
		const rangeEnd = Math.min(indexMap.length, rangeStart + Math.max(0, normalizeRangeNumber(range.length)));
		let currentStart = null;
		let currentEnd = null;

		for (let i = rangeStart; i < rangeEnd; i++) {
			const mappedIndex = indexMap[i];
			if (!Number.isFinite(mappedIndex)) {
				flushRange(range, currentStart, currentEnd);
				currentStart = null;
				currentEnd = null;
				continue;
			}

			if (currentStart === null) {
				currentStart = mappedIndex;
				currentEnd = mappedIndex;
			} else if (mappedIndex === currentEnd + 1) {
				currentEnd = mappedIndex;
			} else {
				flushRange(range, currentStart, currentEnd);
				currentStart = mappedIndex;
				currentEnd = mappedIndex;
			}
		}

		flushRange(range, currentStart, currentEnd);
	});

	return remappedRanges;
}

function convertDraftBlockColorSyntax(block = {}) {
	const text = block.text == null ? "" : `${block.text}`;
	const indexMap = new Array(text.length);
	const colorRanges = [];
	const stack = [];
	let nextText = "";
	let lastIndex = 0;
	let match;
	let converted = false;
	COLOR_TAG_PATTERN.lastIndex = 0;

	while ((match = COLOR_TAG_PATTERN.exec(text)) !== null) {
		nextText = appendMappedText(text, lastIndex, match.index, nextText, indexMap);

		if (match[1] !== undefined) {
			const style = getAmbifiDraftColorStyle(match[1]);
			if (style) {
				stack.push({
					offset: nextText.length,
					style
				});
				converted = true;
			} else {
				nextText = appendMappedText(text, match.index, COLOR_TAG_PATTERN.lastIndex, nextText, indexMap);
			}
		} else if (stack.length > 0) {
			const colorRange = stack.pop();
			colorRanges.push({
				offset: colorRange.offset,
				length: nextText.length - colorRange.offset,
				style: colorRange.style
			});
			converted = true;
		} else {
			nextText = appendMappedText(text, match.index, COLOR_TAG_PATTERN.lastIndex, nextText, indexMap);
		}

		lastIndex = COLOR_TAG_PATTERN.lastIndex;
	}

	nextText = appendMappedText(text, lastIndex, text.length, nextText, indexMap);

	if (!converted || stack.length > 0) {
		return block;
	}

	return {
		...block,
		text: nextText,
		inlineStyleRanges: colorRanges.filter((range) => range.length > 0)
			.concat(remapInlineStyleRanges(block.inlineStyleRanges || [], indexMap))
	};
}

export function convertAmbifiMarkdownColorSyntaxToDraftRaw(rawDraftObject = {}) {
	if (!rawDraftObject || !Array.isArray(rawDraftObject.blocks)) {
		return rawDraftObject;
	}

	return {
		...rawDraftObject,
		blocks: rawDraftObject.blocks.map(convertDraftBlockColorSyntax)
	};
}

export function getAmbifiMarkdownColorDraftStyleItems(rawDraftObject = {}) {
	const styleItems = {};
	const blocks = rawDraftObject && Array.isArray(rawDraftObject.blocks) ? rawDraftObject.blocks : [];

	blocks.forEach((block) => {
		const inlineStyleRanges = block && Array.isArray(block.inlineStyleRanges) ? block.inlineStyleRanges : [];
		inlineStyleRanges.forEach((range) => {
			const style = range && range.style;
			if (typeof style !== "string" || !style.startsWith(DRAFT_COLOR_STYLE_PREFIX)) {
				return;
			}

			const color = sanitizeAmbifiMarkdownColor(style.slice(DRAFT_COLOR_STYLE_PREFIX.length));
			if (!color) {
				return;
			}

			styleItems[style] = {
				open: () => `[color=${color}]`,
				close: () => "[/color]"
			};
		});
	});

	return styleItems;
}

export function stripAmbifiMarkdownColorSyntax(value = "") {
	const text = value == null ? "" : `${value}`;
	return text
		.replace(COLOR_OPEN_PATTERN, "")
		.replace(COLOR_CLOSE_PATTERN, "");
}
